import { getHomeUrl } from '@/utils/cp-context';
import type { Breadcrumb } from '@ps-refarch-ux/neon';
import { PUBLISHED_FILTER_VALUES } from '@/shared/filters/filters.type';
import { STAFF_GRID_PAGINATION_THRESHOLD } from './grid-constants';

export { STATUS_FILTER_VALUES, PUBLISHED_FILTER_VALUES } from '@/shared/filters/filters.type';

export enum EMPTY_PLAN_OF_STUDY_TEXT {
  Title = 'No plans of studies',
  Body = 'Create a plan of study to organize courses and support students in planning their academic journey.',
  Button = 'Create plan of study',
  NoFilterResultsTitle = 'No results found',
  NoFilterResultsBody = '',
  NoFilterResultsButton = 'Clear filters',
}

export enum EMPTY_PLAN_OF_STUDY_ID {
  Button = 'no-plan-of-study',
  ClearFiltersButton = 'plans-of-study-clear-filters',
}

export enum PLANS_OF_STUDY_ID {
  Breadcrumbs = 'plans-of-study-breadcrumbs',
  Button = 'create-plan-of-study',
  SearchField = 'plans-of-study-search',
  FiltersButton = 'plans-of-study-filters',
  FilterDrawer = 'plans-of-study-filter-drawer',
  FilterStatusSelect = 'filter-status-select',
  FilterHideInactiveCheckbox = 'filter-hide-inactive',
  FilterPublishedSchoolsSelect = 'filter-published-schools',
  FilterClassYearFromSelect = 'filter-class-year-from',
  FilterClassYearToSelect = 'filter-class-year-to',
  FilterButtonFooter = 'filter-button-footer',
  FilterApplyButton = 'filter-apply-button',
  FilterCancelButton = 'filter-cancel-button',
  PublishedSchoolsInfoTooltip = 'published-schools-info-tooltip',
  ActiveSchoolsInfoTooltip = 'active-schools-info-tooltip',
  DetailDeleteDisabledTooltip = 'pos-detail-delete-disabled-tooltip-target',
  DetailEditLockedTooltip = 'pos-detail-edit-plan-locked-tooltip',
  DetailPublishLockedTooltip = 'pos-detail-publish-update-locked-tooltip',
}

export enum PLANS_OF_STUDY_TEXT {
  BreadcrumbHome = 'Home',
  Title = 'Plans of study',
  Button = 'Create plan of study',
  Filters = 'Filters',
  SearchPlaceholder = 'Search by plan name',
  NoDescription = 'No description',
  FilterStatusLabel = 'Status',
  FilterHideInactiveLabel = 'Hide inactive plans',
  FilterPublishedSchoolsLabel = 'Published schools',
  FilterClassYearsLabel = 'Class years',
  FilterClassYearsTo = 'to',
  FilterApply = 'Apply',
  PublishedSchoolsTooltip = 'Number of schools where the plan is published.',
  ActiveSchoolsTooltip = 'Number of schools where the plan is active for students.',
  PublishedSchoolsInfoButton = 'Published schools info',
  PublishedSchoolsInfoAriaLabel = 'Published schools information',
  ActiveSchoolsInfoButton = 'Active schools info',
  ActiveSchoolsInfoAriaLabel = 'Active schools information',
  DetailDeleteDisabledTooltip = 'Published plans cannot be deleted.',
  /** PoS with a scheduled publish — disable edit entry points (list + detail). */
  ScheduledPublishEditUnavailable = 'Editing is unavailable while this plan has a scheduled publication.',
  /** Plan detail — Additional details when no student groups are selected. */
  DetailStudentGroupsNoRestrictions = 'No group restrictions',
  /** Plan detail — GET school-student-groups failed before any data was available (plan still has restriction IDs). */
  DetailStudentGroupsLoadFailed = 'Unable to load student groups.',
}

export enum PLANS_OF_STUDY_ICON {
  Add = 'add',
  Filters = 'settings-tune',
}

export const getPlansOfStudyBreadcrumbs: Array<Breadcrumb> = [
  { text: PLANS_OF_STUDY_TEXT.BreadcrumbHome, url: getHomeUrl() },
  { text: PLANS_OF_STUDY_TEXT.Title, url: '#' },
] as const;

export const FILTER_STATUS_OPTIONS = [
  { text: 'All', value: PUBLISHED_FILTER_VALUES.EMPTY },
  { text: 'Published', value: PUBLISHED_FILTER_VALUES.YES },
  { text: 'Unpublished', value: PUBLISHED_FILTER_VALUES.NO },
];

export const FILTER_PUBLISHED_SCHOOLS_OPTIONS = [{ text: 'All schools', value: 'all' }];

export const DEFAULT_PLANS_OF_STUDY_PAGE_SIZE = 25;
export const PLANS_OF_STUDY_PAGE_SIZE_OPTIONS = '25,50,100,200';
export const PLANS_OF_STUDY_FETCH_PAGE_SIZE = 100;
export const PLANS_OF_STUDY_PAGINATION_THRESHOLD = STAFF_GRID_PAGINATION_THRESHOLD;
export const PLANS_OF_STUDY_GRID_HEADER_CLASS = 'neon-data-grid-header plans-of-study-header';
export const PLANS_OF_STUDY_GRID_HEADER_CLASS_RIGHT =
  'neon-data-grid-header plans-of-study-header plans-of-study-header-right';

export const FILTER_CLASS_YEAR_OPTIONS = (() => {
  const today = new Date();
  const y = today.getFullYear();
  const semesterLimit = new Date(y, 6, 15);
  const cur = today < semesterLimit ? y : y + 1;
  const options: Array<{ text: string; value: string }> = [];
  for (let yr = cur - 6; yr <= cur + 12; yr++) {
    options.push({ text: String(yr), value: String(yr) });
  }
  return options;
})();

/**
 * Plan id for flat requirement URLs (`/courses/requirements/:requirementId?p=:planId`).
 * Required for API `usePlanOfStudyDetail(planId)`.
 */
export const POS_PLAN_QUERY = 'p';

/**
 * Short query for requirement detail URLs (`t=c` = Select courses).
 * Legacy `?tab=select-courses` is still read for bookmarks.
 */
export const POS_REQUIREMENT_TAB_QUERY = 't';
export const POS_REQUIREMENT_TAB_COURSES = 'c';

/** Append `&t=c` when the URL already has a query string (e.g. after `?p=planId`). */
export function appendPosSelectCoursesTab(): string {
  return `&${POS_REQUIREMENT_TAB_QUERY}=${POS_REQUIREMENT_TAB_COURSES}`;
}

/** @deprecated Use `appendPosSelectCoursesTab` after `?p=` or full `requirementDetail` path. */
export function posRequirementSelectCoursesSearch(): string {
  return `?${POS_REQUIREMENT_TAB_QUERY}=${POS_REQUIREMENT_TAB_COURSES}`;
}

/** Requirement detail (edit) page — NAVI-39681 (aligned with POS / create-requirement copy). */
export const REQUIREMENT_DETAIL_UI = {
  requirementTag: 'Requirement',
  titleFallback: 'Requirement',
  selectCoursesIntro:
    'Create a course rule to define the courses that can satisfy this requirement. You can create multiple rule types within this requirement to show students the most relevant options.',
  previewCourses: 'Preview courses',
  previewCoursesTooltip: 'Add courses to preview',
  /** Shown when the requirement has linked courses but preview UI is not implemented yet. */
  previewCoursesNotAvailableTooltip: 'Preview is not available yet.',
  previewCoursesTargetId: 'plan-requirement-detail-preview-target',
  previewCoursesHasCoursesTargetId: 'plan-requirement-detail-preview-has-courses-target',
  mandatedCourses: 'Mandated courses',
  mandatedCoursesDescription: 'Students must take these courses to meet the requirement.',
  groupsOfCourses: 'Groups of courses',
  groupsOfCoursesDescription:
    'Students must select all courses in a group, such as Spanish 1, 2, and 3 or French 1, 2, and 3.',
  choicesByGrade: 'Choices by grade',
  choicesByGradeDescription:
    'Students must choose from course options presented by grade level, such as English 9 or Honors English 9 in grade 9.',
  itemsFromAList: 'Items from a list',
  itemsFromAListDescription:
    'Students must choose courses from a list to fulfill the required number of credits.',
  /** Select courses tab — alternates requirement only (Items from a list). */
  selectCoursesIntroAlternates:
    'Map courses students can use as alternates. Create one or two lists from available items using and/or logic.',
  /** Details tab — alternates requirement only (metadata here; course mapping on Select courses). */
  detailsCardIntroAlternates:
    'Edit this requirement’s name, credits, and description. Alternate courses are mapped on the Select courses tab.',
  createRule: 'Create rule',
  whenCreatingRulesTitle: 'When creating course rules:',
  whenCreatingRulesBullets: [
    "Add all applicable courses to a rule. Only courses in a school's catalog will be displayed to students.",
    'Inactive courses can be included for historical reference but won\u2019t be shown as options.',
    'Grade-level eligibility is enforced when students select courses.',
  ],
} as const;

/** Create requirement page + requirement detail shell (aligned with existing POS / add-course patterns). */
export const CREATE_REQUIREMENT_UI = {
  pageTitle: 'Create requirement',
  /** Shown inside the white form card above the requirement name field. */
  cardIntro:
    'Enter requirement details to guide students, including the name, the number of credits needed to fulfill it, and a brief description.',
  creditsHelper: 'Enter a number such as 0.5, 2.5, or 5.',
  saveAndSelectCourses: 'Save and select courses',
  substitutionCheckbox:
    'Courses in this requirement are electives that can be substituted with alternates.',
  toastSubheading: 'Next, select courses for this requirement.',
  toastPlansOfStudy: 'Plans of study',
  toastSelectCourses: 'Select courses',
  detailsTab: 'Details',
  selectCoursesTab: 'Select courses',
  selectCoursesEmpty: 'No courses have been added to this requirement yet.',
} as const;

/** Plan of study detail — requirements table + delete requirement (NAVI-39680). */
export const PLAN_REQUIREMENTS_UI = {
  reorderRequirements: 'Reorder requirements',
  tableAriaLabel: 'Plan requirements',
  columnName: 'Name',
  columnCourseCount: 'Course count',
  columnRequiredCredits: 'Required credits',
  columnDescription: 'Description',
  columnAction: 'Action',
  courseCountWarningTooltip: 'Courses must be selected for requirement.',
  deleteModalTitle: 'Delete plan requirement?',
  deleteModalBody:
    'Deleting the selected requirement will remove it from this plan of study. This action cannot be undone.',
  deleteRequirement: 'Delete requirement',
  /** Icon-only utility delete — matches plans-of-study grid (`NeonTooltip` hover label). */
  deleteUtilityTooltip: 'Delete',
  descriptionEmpty: '—',
  /**
   * Synthetic alternates row — matches `DetailStatBlock` info control.
   * Describes **student** completion behavior only; the card “X total credits” tag still sums every
   * requirement on the plan (including alternates) for staff.
   */
  alternatesInfoTooltip:
    'This requirement is added automatically for students. It does not count toward their credit requirements and allows them to select alternate courses if needed.',
  /** `NeonButton` `dataText` when `dataShowText` is false (same pattern as Published schools). */
  alternatesInfoButton: 'Alternates info',
  alternatesInfoIconAriaLabel: 'About the alternates requirement',
} as const;
